import { Button, Callout, H3, Intent, PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { DialogConnectionTile } from 'src/components/DialogConnectionTile';
import { TEST_IDS } from 'src/constants/testIds';
import {
  generateModelNamedGraph,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  AddModelType,
  useAddModelType,
} from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import { useLocalModel } from 'src/containers/ProjectModelDialog/hooks/useLocalModelValue';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { ImportModelPanel } from 'src/containers/ProjectModelDialog/ImportModelPanel';
import { LocalModelPanel } from 'src/containers/ProjectModelDialog/LocalModelPanel';
import { UploadModelFileTile } from 'src/containers/ProjectModelDialog/UploadModelFileTile';
import { UploadModelPanel } from 'src/containers/ProjectModelDialog/UploadModelPanel';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type ModelTypePanelProps = {
  // empty
};

export const ModelTypePanel: React.VFC<PanelProps<ModelTypePanelProps>> = ({
  openPanel,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const { setAddModelType } = useAddModelType();

  const { setNamespace: setLocalNamespace } = useLocalModel();
  const { setFile, setNamedGraphIri } = useUploadModel();

  const handleOpenLocalModelPanel = () => {
    setAddModelType(AddModelType.LOCAL);
    setLocalNamespace(
      getDefaultProjectModelPrefix({ name: project?.name || '' })
    );
    openPanel({ renderPanel: LocalModelPanel });
  };

  const handleOpenUploadModelPanel = (file: File) => {
    setAddModelType(AddModelType.UPLOAD);
    setFile(file);
    if (project) {
      setNamedGraphIri(generateModelNamedGraph(project, { name: 'example' }));
    }
    openPanel({ props: { file }, renderPanel: UploadModelPanel });
  };

  const handleOpenImportModelPanel = () => {
    setAddModelType(AddModelType.IMPORT);
    openPanel({ renderPanel: ImportModelPanel });
  };

  return (
    <div className="sd-model-type-panel">
      <div className="sd-model-type-panel__tile-wrapper">
        <Callout className="sd-model-type-panel__tile" intent={Intent.NONE}>
          <H3>{copy.components.projectModelDialog.modelTypePanel.local}</H3>
          <div className="sd-model-type-panel__tile-subtitle">
            {copy.components.projectModelDialog.modelTypePanel.localSubtitle}
          </div>
          <Button
            data-testid={
              TEST_IDS.projectModelDialog.modelTypePanel.localModel
                .proceedButton
            }
            intent={Intent.PRIMARY}
            large
            onClick={handleOpenLocalModelPanel}
            text={
              copy.components.projectModelDialog.modelTypePanel.proceedButton
            }
          />
        </Callout>
        <UploadModelFileTile onSelectFile={handleOpenUploadModelPanel} />
        <DialogConnectionTile
          cardClassName="sd-model-type-panel__tile"
          handleProceedWithConnection={handleOpenImportModelPanel}
          proceedButtonTestId={
            TEST_IDS.projectModelDialog.modelTypePanel.importModel.proceedButton
          }
          switchConnectionTestId={
            TEST_IDS.projectModelDialog.modelTypePanel.importModel
              .switchConnection
          }
          title={copy.components.projectModelDialog.modelTypePanel.import}
        />
      </div>
    </div>
  );
};
